/**
 * Client Invoice (Payment Application) API Service - RTK Query
 * BuilderBel Construction Project Management System
 */

import { ApiResponse } from '../types';
import { baseApi } from './api';
import type {
  AddChangeOrderToSOVRequest,
  BillingSummary,
  CertifyPaymentApplicationRequest,
  CreatePaymentApplicationRequest,
  PaymentApplication,
  PaymentApplicationListParams,
  PaymentApplicationListResponse,
  PaymentApplicationStats,
  RecordPaymentRequest,
  RejectPaymentApplicationRequest,
  UpdatePaymentApplicationRequest,
} from '../types/clientInvoice';

/**
 * Client Invoice API Endpoints
 */
export const clientInvoiceApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // ============================================================================
    // LIST & STATS
    // ============================================================================

    getClientInvoices: builder.query<
      ApiResponse<PaymentApplicationListResponse>,
      { projectId: string; params?: PaymentApplicationListParams }
    >({
      query: ({ projectId, params }) => ({
        url: `/projects/${projectId}/client-invoices`,
        params,
      }),
      providesTags: ['ClientInvoice'],
    }),

    getClientInvoiceStats: builder.query<
      ApiResponse<PaymentApplicationStats>,
      { projectId: string }
    >({
      query: ({ projectId }) => `/projects/${projectId}/client-invoices/stats`,
      providesTags: ['ClientInvoice'],
    }),

    getBillingSummary: builder.query<ApiResponse<BillingSummary>, { projectId: string }>({
      query: ({ projectId }) => `/projects/${projectId}/client-invoices/billing-summary`,
      providesTags: ['ClientInvoice'],
    }),

    // ============================================================================
    // CRUD
    // ============================================================================

    getClientInvoice: builder.query<
      ApiResponse<PaymentApplication>,
      { projectId: string; applicationId: string }
    >({
      query: ({ projectId, applicationId }) =>
        `/projects/${projectId}/client-invoices/${applicationId}`,
      providesTags: (_result, _error, { applicationId }) => [
        { type: 'ClientInvoice', id: applicationId },
      ],
    }),

    createClientInvoice: builder.mutation<
      ApiResponse<PaymentApplication>,
      { projectId: string; data: CreatePaymentApplicationRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/client-invoices`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['ClientInvoice'],
    }),

    updateClientInvoice: builder.mutation<
      ApiResponse<PaymentApplication>,
      { projectId: string; applicationId: string; data: UpdatePaymentApplicationRequest }
    >({
      query: ({ projectId, applicationId, data }) => ({
        url: `/projects/${projectId}/client-invoices/${applicationId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { applicationId }) => [
        'ClientInvoice',
        { type: 'ClientInvoice', id: applicationId },
      ],
    }),

    deleteClientInvoice: builder.mutation<
      ApiResponse<null>,
      { projectId: string; applicationId: string }
    >({
      query: ({ projectId, applicationId }) => ({
        url: `/projects/${projectId}/client-invoices/${applicationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ClientInvoice'],
    }),

    // ============================================================================
    // WORKFLOW ACTIONS
    // ============================================================================

    submitClientInvoice: builder.mutation<
      ApiResponse<PaymentApplication>,
      { projectId: string; applicationId: string }
    >({
      query: ({ projectId, applicationId }) => ({
        url: `/projects/${projectId}/client-invoices/${applicationId}/submit`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { applicationId }) => [
        'ClientInvoice',
        { type: 'ClientInvoice', id: applicationId },
      ],
    }),

    certifyClientInvoice: builder.mutation<
      ApiResponse<PaymentApplication>,
      { projectId: string; applicationId: string; data: CertifyPaymentApplicationRequest }
    >({
      query: ({ projectId, applicationId, data }) => ({
        url: `/projects/${projectId}/client-invoices/${applicationId}/certify`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { applicationId }) => [
        'ClientInvoice',
        { type: 'ClientInvoice', id: applicationId },
      ],
    }),

    rejectClientInvoice: builder.mutation<
      ApiResponse<PaymentApplication>,
      { projectId: string; applicationId: string; data: RejectPaymentApplicationRequest }
    >({
      query: ({ projectId, applicationId, data }) => ({
        url: `/projects/${projectId}/client-invoices/${applicationId}/reject`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { applicationId }) => [
        'ClientInvoice',
        { type: 'ClientInvoice', id: applicationId },
      ],
    }),

    recordClientInvoicePayment: builder.mutation<
      ApiResponse<PaymentApplication>,
      { projectId: string; applicationId: string; data: RecordPaymentRequest }
    >({
      query: ({ projectId, applicationId, data }) => ({
        url: `/projects/${projectId}/client-invoices/${applicationId}/record-payment`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { applicationId }) => [
        'ClientInvoice',
        { type: 'ClientInvoice', id: applicationId },
      ],
    }),

    addChangeOrderToClientInvoiceSOV: builder.mutation<
      ApiResponse<PaymentApplication>,
      { projectId: string; applicationId: string; data: AddChangeOrderToSOVRequest }
    >({
      query: ({ projectId, applicationId, data }) => ({
        url: `/projects/${projectId}/client-invoices/${applicationId}/add-change-order`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { applicationId }) => [
        'ClientInvoice',
        { type: 'ClientInvoice', id: applicationId },
      ],
    }),
  }),
});

export const {
  useGetClientInvoicesQuery,
  useGetClientInvoiceStatsQuery,
  useGetBillingSummaryQuery,
  useGetClientInvoiceQuery,
  useCreateClientInvoiceMutation,
  useUpdateClientInvoiceMutation,
  useDeleteClientInvoiceMutation,
  useSubmitClientInvoiceMutation,
  useCertifyClientInvoiceMutation,
  useRejectClientInvoiceMutation,
  useRecordClientInvoicePaymentMutation,
  useAddChangeOrderToClientInvoiceSOVMutation,
} = clientInvoiceApi;
